import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
  meta: {
    current_page: 1,
    total_pages: 1,
    total_count: 0,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a2.id - a1.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP, INBOX_TYPES.WHATSAPP_WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    const liveChatChannelTypes = [INBOX_TYPES.WEB];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONGOING, liveChatChannelTypes);
  },
  getAllCampaigns: _state => {
    return _state.records.sort((a1, a2) => a2.id - a1.id);
  },
};

export const actions = {
  get: async function getCampaigns({ commit }, params = {}) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get(params);
      if (response.data.payload) {
        commit(types.SET_CAMPAIGNS, response.data.payload);
        commit('SET_CAMPAIGNS_META', response.data.meta);
      } else {
        commit(types.SET_CAMPAIGNS, response.data);
      }
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  pause: async ({ commit }, id) => {
    try {
      await CampaignsAPI.pause(id);
      commit(types.EDIT_CAMPAIGN, { id, campaign_status: 'paused' });
    } catch (error) {
      throw new Error(error);
    }
  },
  resume: async ({ commit }, id) => {
    try {
      await CampaignsAPI.resume(id);
      commit(types.EDIT_CAMPAIGN, { id, campaign_status: 'in_progress' });
    } catch (error) {
      throw new Error(error);
    }
  },
  fetchMetrics: async ({ commit }, id) => { // eslint-disable-line no-unused-vars
    try {
      const response = await CampaignsAPI.getMetrics(id);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  exportDeliveries: async ({ commit }, id) => { // eslint-disable-line no-unused-vars
    try {
      const response = await CampaignsAPI.exportDeliveries(id);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  fetchDashboardMetrics: async ({ commit }, params = {}) => {
    try {
      const response = await CampaignsAPI.getDashboardMetrics(params);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
  fetchLimits: async ({ commit }, inboxId) => { // eslint-disable-line no-unused-vars
    try {
      const response = await CampaignsAPI.getLimits(inboxId);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  SET_CAMPAIGNS_META(_state, data) {
    _state.meta = data;
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
